import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import H from '../components/texts/h/H';
import Card from '../components/card/Card';
import Table from '../components/table/table';
import Icon from '../components/icon/Icon';
import P from '../components/texts/p/P';
import Button from '../components/button/Button';
import { getRateTypes, updateRateType } from '../services/GuaranteesService';
import Spinner from '../components/Spinner/spinner';
import RateTypesModal from '../components/modal/RateTypesModal';
import { Toast, useToast } from '../components/toast/toast';
import { routes } from '../routes/routePaths';

interface RateType {
  id: number;
  name: string;
  baseValue: number;
}

const RateTypes = () => {
  const navigate = useNavigate();
  const [rateTypes, setRateTypes] = useState<RateType[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRateType, setSelectedRateType] = useState<RateType | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchRateTypes();
  }, []);

  const fetchRateTypes = async () => {
    try {
      setLoading(true);
      const response = await getRateTypes();
      if (response.success && 'data' in response) {
        setRateTypes(response.data);
      }
    } catch (error) {
      console.error('Error fetching rate types:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate(routes.settings);
  };

  const handleEditClick = (row: RateType) => {
    setSelectedRateType(row);
    setIsModalOpen(true);
  };

  const handleRowClicked = (row: RateType) => {
    handleEditClick(row);
  };

  const handleSave = async (id: number, baseValue: number) => {
    try {
      setIsSaving(true);
      const response = await updateRateType(id, baseValue);
      
      if (response.success) {
        useToast.success({
          title: 'Tipo de tasa actualizado correctamente',
          id: 'rate-type-toast',
          description: '',
          closeButton: false,
          autoClose: 3000,
        });
        
        setIsModalOpen(false);
        setSelectedRateType(null);
        fetchRateTypes();
      } else {
        useToast.error({
          title: 'Error al actualizar el tipo de tasa',
          id: 'rate-type-toast',
          description: 'Por favor, intentá nuevamente',
          closeButton: false,
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error('Error saving rate type:', error);
      useToast.error({
        title: 'Error al actualizar el tipo de tasa',
        id: 'rate-type-toast',
        description: 'Por favor, intentá nuevamente',
        closeButton: false,
        autoClose: 3000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedRateType(null);
  };

  const columns = [
    {
      name: 'Nombre',
      selector: (row: RateType) => (
        <Card>
          <P type='3' variant='neutral'>
            {row.name}
          </P>
        </Card>
      ),
    },
    {
      name: 'Valor base',
      selector: (row: RateType) => (
        <Card>
          <P type='3' variant='neutral'>
            {row.baseValue}
          </P>
        </Card>
      ),
      center: true,
    },
    {
      name: '',
      cell: (row: RateType) => (
        <Card>
          <Icon
            name='edit_outline'
            width={20}
            height={20}
            cursor='pointer'
            variant='info'
            onClick={() => handleEditClick(row)}
          />
        </Card>
      ),
      center: true,
    },
  ];

  return (
    <Card variant='neutral' modifier='inverted' spacing='4' height='-webkit-fill-available' width='85vw'>
      <Toast id='rate-type-toast' />
      
      <RateTypesModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        rateType={selectedRateType}
        onSave={handleSave}
        loading={isSaving}
      />

      <Card display='flex' alignItems='center' gap='16px' margin={{ bottom: '3' }}>
        <Button 
          icon='arrow_left_outline'
          variant='tertiary'
          size='sm'
          iconAlign='center'
          onClick={handleGoBack}
          aria-label="Volver a configuración"
        />
        <H withMargin={false} type='1' weight='bold' modifier='strong'>
          Tipos de tasas
        </H>
      </Card>
      
      {loading ? (
        <Card display='flex' justifyContent='center' alignItems='center' margin={{ top: '4' }}>
          <Spinner size='lg' />
        </Card>
      ) : (
        <Table
          columns={columns}
          data={rateTypes}
          tableData={{ size: rateTypes.length, totalElements: rateTypes.length }}
          variant='normal'
          onRowClicked={handleRowClicked}
        />
      )}
    </Card>
  );
};

export default RateTypes;